// All TanStack Query keys in one place
// Format: [domain, ...identifiers]
import type { StockFilters, StockPaging, CustomerIntelFilters, CustomerIntelPaging, PosManagerOverrideListParams,PosReportFilters,RegistersReportFilters, RegistersReportPaging,ReportRangeSelection } from '@/types'

export const queryKeys = {
  // Auth
  me: ['me'] as const,
  rolePermissions: () => ['rolePermissions'] as const,

  // Organization / Shop
  orgBySlug: (slug: string) => ['orgBySlug', slug] as const,
  // Branding-only projection of GET /organizations/:id (public endpoint).
  organizationBranding: (orgId: string) => ['organizationBranding', orgId] as const,
  shops: (orgId: string) => ['shops', orgId] as const,
  shop: (shopId: string) => ['shop', shopId] as const,
  users: (orgId: string) => ['users', orgId] as const,
  overrideApprovers: (action: string) => ['override-approvers', action] as const,

  // Catalog
  products: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['products', shopId, params] : ['products', shopId],
  productsByBrand: (brandId: string) => ['products', 'brand', brandId] as const,
  // Domain-wide prefixes for post-create invalidation — a create should refresh
  // every list/search under the domain (incl. the "already added" lookups) so
  // new entities appear without a page refresh. Pair with refetchType: 'all' so
  // even queries whose modal just closed (went inactive) refetch.
  productsAll: () => ['products'] as const,
  brandsAll: () => ['brands'] as const,
  strainsAll: () => ['strains'] as const,
  ecomVocab: ['ecom-vocab'] as const,
  productMerch: (productId: string) => ['product-merch', productId] as const,
  ecomSite: ['ecom-site'] as const,
  ecomThemes: ['ecom-themes'] as const,
  ecomDomainConfig: ['ecom-domain-config'] as const,
  /** Live DNS/certificate check of one custom domain (POST …/verify, polled while it isn't live). */
  ecomDomainCheck: (domainId: string) => ['ecom-domain-check', domainId] as const,
  ecomSitePayments: ['ecom-site-payments'] as const,
  ecomReadiness: ['ecom-readiness'] as const,
  product: (productId: string) => ['product', productId] as const,
  // Prefix for cancel/getQueriesData over every product-detail entry.
  productPrefix: () => ['product'] as const,
  // Product detail keyed by shop (sale configs differ per shop).
  productDetail: (productId: string | null | undefined, shopId: string) => ['product', productId, shopId] as const,
  classification: (id: string | null | undefined) => ['classification', id] as const,
  strain: (id: string | null | undefined) => ['strain', id] as const,
  tag: (id: string | null | undefined) => ['tag', id] as const,
  supplier: (id: string | null | undefined) => ['supplier', id] as const,
  supplierTypes: () => ['supplierTypes'] as const,
  uomsForCategory: (categoryId: string | null) => ['uoms', 'forCategory', categoryId] as const,
  categories: (shopId: string) => ['categories', shopId] as const,
  brands: (shopId: string) => ['brands', shopId] as const,
  brand: (brandId: string) => ['brand', brandId] as const,
  platformBrandSearch: (term: string) => ['platformBrands', 'search', term] as const,
  platformStrainSearch: (term: string) => ['platformStrains', 'search', term] as const,
   classifications: (shopId: string) => ['classifications', shopId] as const,
   classificationsMap: () => ['classifications-map'] as const,
   // id → isMj lookup (distinct from classificationsMap, which is id → name).
   classificationsIsMjMap: () => ['classifications-isMj-map'] as const,
   // Unscoped product list for picker dropdowns (mapping dialogs).
   allProductsPicker: () => ['products', 'picker'] as const,
   productPickerSearch: (term: string) => ['products', 'picker', term] as const,
   // Normalized sale-config options for the mapping dialogs — separate from
   // saleConfigs(productId), whose cache holds the raw POS shape.
   saleConfigOptions: (productId: string) => ['saleConfigOptions', productId] as const,
   // Parsed LocalSaleConfig list for the add-inventory dialog (shop-filtered).
   saleConfigsParsed: (productId: string, shopId: string | null | undefined) => ['saleConfigsParsed', productId, shopId] as const,
   // Does inventory / an unmapped package already exist for this sale config?
   inventoryExistence: (shopId: string, saleConfigId: string, cannabis: boolean) => ['inventoryExistence', shopId, saleConfigId, cannabis] as const,
   // Prefix — invalidates every existence check at once after stock is created.
   inventoryExistenceAll: () => ['inventoryExistence'] as const,
   // Effective is-marijuana flag for a product (own or category classification).
   productIsMj: (productId: string | null | undefined, inlineClassificationId: string | null | undefined) => ['productIsMj', productId, inlineClassificationId] as const,
   metrcPackagePickerSearch: (shopId: string, term: string) => ['metrcPackages', 'picker', shopId, term] as const,
   // name/categoryId subset of a product (mapping dialogs) — separate from
   // product(id), whose cache holds the full Product shape.
   productBasic: (id: string | null | undefined) => ['productBasic', id] as const,
   strains: (shopId: string) => ['strains', shopId] as const,
   tags: (shopId: string) => ['tags', shopId] as const,
   tagOptions: () => ['tags', 'options'] as const,
   catalogCategoryGroups: () => ['catalogCategoryGroups'] as const,
   dualBrandSearch: (term: string) => ['brands', 'dual-search', term] as const,
   initialBrands: () => ['brands', 'initial'] as const,
   catalogProductSearch: (brandSlug: string, term: string) => ['catalogProducts', brandSlug, term] as const,
   dualStrainSearch: (term: string) => ['strains', 'dual-search', term] as const,
   categoryUoms: (categoryId: string) => ['uoms', 'category', categoryId] as const,
   allUoms: () => ['uoms', 'all'] as const,
   weightUoms: () => ['weightUoms'] as const,

   // Sale configs
   saleConfigs: (productId: string) => ['saleConfigs', productId] as const,
   saleConfig: (saleConfigId: string) => ['saleConfig', saleConfigId] as const,
   saleConfigsByIds: (ids: string[]) => ['saleConfigsByIds', ...ids.slice().sort()] as const,
   pricingSchemes: (shopId?: string) => shopId ? ['pricingSchemes', shopId] as const : ['pricingSchemes'] as const,
   pricingProductCounts: () => ['pricingProductCounts'] as const,

  // Inventory
  inventory: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['inventory', shopId, params] : ['inventory', shopId],
  inventoryItem: (saleConfigId: string) => ['inventoryItem', saleConfigId] as const,

  // Settings
  storeSettings: (shopId: string) => ['storeSettings', shopId] as const,
  metrcSettings: (shopId: string) => ['metrcSettings', shopId] as const,

  supplierSearch: (term: string) => ['suppliers', 'search', term] as const,
  suppliers: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['suppliers', shopId, params] : ['suppliers', shopId],
  pricingTiers: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['pricingTiers', shopId, params] : ['pricingTiers', shopId],
  stock: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['stock', shopId, params] : ['stock', shopId],
  adjustments: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['adjustments', shopId, params] : ['adjustments', shopId],
  packages: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['packages', shopId, params] : ['packages', shopId],
  reconciliations: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['reconciliations', shopId, params] : ['reconciliations', shopId],
  reconciliationSummary: (shopId: string | null | undefined) => ['reconciliationSummary', shopId] as const,
  reconciliationRecord: (id: string | null | undefined) => ['reconciliationRecord', id] as const,
  transfer: (id: string | null | undefined) => ['transfer', id] as const,
  metrcPackage: (id: string | null | undefined) => ['package', id] as const,
  transfers: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['transfers', shopId, params] : ['transfers', shopId],

  // UOM
  uoms: (params?: Record<string, unknown>) =>
    params ? ['uoms', params] : ['uoms'],
  uom: (id: string) => ['uom', id] as const,

  saleConfigDetail: (saleConfigId: string) => ['saleConfigDetail', saleConfigId] as const,
  packageAdjustments: (packageId: string, shopId: string) => ['packageAdjustments', packageId, shopId] as const,
  inventoryDetail: (inventoryId: string) => ['inventoryDetail', inventoryId] as const,
  inventoryAdjustments: (shopId: string, params?: Record<string, unknown>) =>
    params ? ['inventoryAdjustments', shopId, params] : ['inventoryAdjustments', shopId],
  userProfile: (userId: string) => ['userProfile', userId] as const,
  transferDeliveries: (transferId: string, shopId: string) => ['transferDeliveries', transferId, shopId] as const,

  // Customers + customer groups are ORG-wide (shared across shops), so they are
  // NOT keyed by shop — keying by shop caused redundant refetches on shop switch.
  customers: (params?: Record<string, unknown>) =>
    params ? (['customers', params] as const) : (['customers'] as const),
  customer: (customerId: string) => ['customer', customerId] as const,
  /** The customer's storefront login as staff see it (GET /customers/:id/online-account). */
  customerOnlineAccount: (customerId: string) => ['customer', customerId, 'online-account'] as const,
  customerGroups: () => ['customerGroups'] as const,
  customerGroup: (groupId: string) => ['customerGroup', groupId] as const,
  customerGroupRules: (groupId: string) => ['customerGroupRules', groupId] as const,
  customerGroupMembers: (groupId: string, params?: Record<string, unknown>) =>
    params ? ['customerGroupMembers', groupId, params] : ['customerGroupMembers', groupId],
  queue: (shopId: string) => ['queue', shopId] as const,
  customerInsights: () => ['customerInsights'] as const,

  // Loyalty
  loyaltyProgram: () => ['loyaltyProgram'] as const,
  loyaltyTiers: () => ['loyaltyTiers'] as const,
  loyaltyRules: () => ['loyaltyRules'] as const,
  loyaltyCampaigns: () => ['loyaltyCampaigns'] as const,
  loyaltyEvents: (params?: Record<string, unknown>) =>
    params ? ['loyaltyEvents', params] : ['loyaltyEvents'] as const,
  customerLoyaltyProfile: (customerId: string) => ['customerLoyaltyProfile', customerId] as const,
  customerLoyaltyEvents: (customerId: string, params?: Record<string, unknown>) =>
    params ? ['customerLoyaltyEvents', customerId, params] : ['customerLoyaltyEvents', customerId] as const,

  // POS
  posFeatureFlags: () => ['posFeatureFlags'] as const,
  posFeatureFlagEnabled: (key: string) => ['posFeatureFlagEnabled', key] as const,
  posRegisters: (locationId: string) => ['posRegisters', locationId] as const,
  posRegister: (id: string) => ['posRegister', id] as const,
  posShifts: (params?: Record<string, unknown>) =>
    params ? ['posShifts', params] : ['posShifts'] as const,
  posShift: (id: string) => ['posShift', id] as const,
  posShiftsNeedingAttention: (params?: Record<string, unknown>) =>
    params ? ['posShiftsNeedingAttention', params] as const : ['posShiftsNeedingAttention'] as const,
  posShiftReport: (id: string) => ['posShiftReport', id] as const,
  posDrawers: (params?: Record<string, unknown>) =>
    params ? ['posDrawers', params] : ['posDrawers'] as const,
  posDrawer: (id: string) => ['posDrawer', id] as const,
  posOrders: (params?: Record<string, unknown>) =>
    params ? ['posOrders', params] : ['posOrders'] as const,
  posOrder: (id: string) => ['posOrder', id] as const,
  posOnlineQueue: (shopId: string) => ['posOnlineQueue', shopId] as const,
  sales: (params?: Record<string, unknown>) =>
    params ? ['sales', params] as const : ['sales'] as const,
  salesMetrcStatus: (orderIds: string[]) => ['salesMetrcStatus', orderIds] as const,
  posOrderNotes: (id: string) => ['posOrderNotes', id] as const,
  posOrderActiveLock: (orderId: string) => ['posOrderActiveLock', orderId] as const,
  posOrderTenders: (orderId: string) => ['posOrderTenders', orderId] as const,
  posTender: (id: string) => ['posTender', id] as const,
  posReceiptScan: (receiptCode: string) => ['posReceiptScan', receiptCode] as const,
  posManagerOverride: (id: string) => ['posManagerOverride', id] as const,
  posManagerOverridesList: (params?: PosManagerOverrideListParams) =>
    params ? ['posManagerOverridesList', params] as const : ['posManagerOverridesList'] as const,
  posReturnQuotesByOrder: (orderId: string) => ['posReturnQuotesByOrder', orderId] as const,
  posReturnQuote: (id: string) => ['posReturnQuote', id] as const,
  posRefundsByOrder: (orderId: string) => ['posRefundsByOrder', orderId] as const,
  posRefundTotalByOrder: (orderId: string) => ['posRefundTotalByOrder', orderId] as const,
  posXReport: (params?: PosReportFilters) => params ? ['posXReport', params] as const : ['posXReport'] as const,
  posZReport: (drawerSessionId: string) => ['posZReport', drawerSessionId] as const,
  posSalesReport: (params?: PosReportFilters) => params ? ['posSalesReport', params] as const : ['posSalesReport'] as const,
  posTipsReport: (params?: PosReportFilters) => params ? ['posTipsReport', params] as const : ['posTipsReport'] as const,
  posAdminStoreSettings: (shopId: string) => ['posAdminStoreSettings', shopId] as const,
  posAdminCashManagement: (shopId: string) => ['posAdminCashManagement', shopId] as const,
  posAdminTaxRules: (params?: Record<string, unknown>) => params ? ['posAdminTaxRules', params] as const : ['posAdminTaxRules'] as const,
  posAdminPotencyTaxBands: () => ['posAdminPotencyTaxBands'] as const,
  posAdminTaxExemptions: (params?: Record<string, unknown>) => params ? ['posAdminTaxExemptions', params] as const : ['posAdminTaxExemptions'] as const,
  posAdminDiscountRules: (params?: Record<string, unknown>) => params ? ['posAdminDiscountRules', params] as const : ['posAdminDiscountRules'] as const,
  posAdminDiscountStackingGroups: () => ['posAdminDiscountStackingGroups'] as const,
  posAdminPurchaseLimits: (params?: Record<string, unknown>) => params ? ['posAdminPurchaseLimits', params] as const : ['posAdminPurchaseLimits'] as const,
  posAdminStateLimitCategories: (params?: Record<string, unknown>) => params ? ['posAdminStateLimitCategories', params] as const : ['posAdminStateLimitCategories'] as const,
  posOrderMetrcStatus: (orderId: string) => ['posOrderMetrcStatus', orderId] as const,
  posAdminComplianceProviders: () => ['posAdminComplianceProviders'] as const,
  metrcWritePause: (shopId: string) => ['metrcWritePause', shopId] as const,
  customerPurchaseLimits: (customerId: string, shopId: string) => ['customerPurchaseLimits', customerId, shopId] as const,
  posAdminJurisdictions: () => ['posAdminJurisdictions'] as const,
  posAdminAgeVerification: (jurisdictionId: string) => ['posAdminAgeVerification', jurisdictionId] as const,
  posReportX: (params?: Record<string, unknown>) => params ? ['posReportX', params] as const : ['posReportX'] as const,
  posReportSales: (params?: Record<string, unknown>) => params ? ['posReportSales', params] as const : ['posReportSales'] as const,
  posReportTips: (params?: Record<string, unknown>) => params ? ['posReportTips', params] as const : ['posReportTips'] as const,
  posPaymentMethodConfigs: (params?: Record<string, unknown>) => params ? ['posPaymentMethodConfigs', params] as const : ['posPaymentMethodConfigs'] as const,
  posPaymentMethodConfig: (id: string) => ['posPaymentMethodConfig', id] as const,
  posPaymentProviderCredentials: (params?: Record<string, unknown>) => params ? ['posPaymentProviderCredentials', params] as const : ['posPaymentProviderCredentials'] as const,
  posStoreCreditAccount: (customerId: string) => ['posStoreCreditAccount', customerId] as const,
  posGiftCard: (code: string) => ['posGiftCard', code] as const,
  posGiftCards: (page: number, search: string) => ['posGiftCards', page, search] as const,
  posAuditBundle: (orderId: string) => ['posAuditBundle', orderId] as const,
  posProducts: (shopId: string, params?: Record<string, unknown>) => params ? ['posProducts', shopId, params] as const : ['posProducts', shopId] as const,

  // Devices
  usbPrinters: () => ['usbPrinters'] as const,
  printAgents: (shopId: string, params?: Record<string, unknown>) => params ? ['printAgents', shopId, params] : ['printAgents', shopId],
  printAgent: (id: string) => ['printAgent', id] as const,
  printAgentStatus: (id: string) => ['printAgentStatus', id] as const,
  printers: (shopId: string, params?: Record<string, unknown>) => params ? ['printers', shopId, params] : ['printers', shopId],
  printer: (id: string) => ['printer', id] as const,
  printJobs: (shopId: string, params?: Record<string, unknown>) => params ? ['printJobs', shopId, params] : ['printJobs', shopId],
  printJob: (id: string) => ['printJob', id] as const,
  printTemplates: (params?: Record<string, unknown>) => params ? ['printTemplates', params] : ['printTemplates'],
  printTemplate: (id: string) => ['printTemplate', id] as const,
  printTemplateVariants: (templateId: string) => ['printTemplateVariants', templateId] as const,

  metrcLocations: (shopId: string) => ['metrcLocations', shopId] as const,
  metrcActiveAlerts: (shopId?: string | null) => ['metrcActiveAlerts', shopId ?? 'org'] as const,
  metrcItem: (id: string | null | undefined) => ['metrcItem', id] as const,
  metrcAdjustReasons: (shopId: string) => ['metrcAdjustReasons', shopId] as const,

  // Analytics reports — keyed on shopId + the range selection
  // object (stable across renders); the queryFn resolves selection → real dates.
  reports: {
    overview: (shopId: string, sel: ReportRangeSelection) => ['reports', 'overview', shopId, sel] as const,
    sales: (shopId: string, sel: ReportRangeSelection) => ['reports', 'sales', shopId, sel] as const,
    cash: (shopId: string, sel: ReportRangeSelection) => ['reports', 'cash', shopId, sel] as const,
    registers: (shopId: string, sel: ReportRangeSelection, filters: RegistersReportFilters, paging: RegistersReportPaging) => ['reports', 'registers', shopId, sel, filters, paging] as const,
    customerIntel: (shopId: string, sel: ReportRangeSelection) => ['reports', 'customer-intel', shopId, sel] as const,
    stock: (shopId: string, sel: ReportRangeSelection) => ['reports', 'stock', shopId, sel] as const,
    stockRows: (shopId: string, sel: ReportRangeSelection, filters: StockFilters & { search?: string }, paging: StockPaging) => ['reports', 'stock', 'rows', shopId, sel, filters, paging] as const,
    customerIntelList: (shopId: string, sel: ReportRangeSelection, filters: CustomerIntelFilters, paging: CustomerIntelPaging) => ['reports', 'customer-intel', 'list', shopId, sel, filters, paging] as const,
    customers: (shopId: string, sel: ReportRangeSelection) => ['reports', 'customers', shopId, sel] as const,
    inventory: (shopId: string, sel: ReportRangeSelection) => ['reports', 'inventory', shopId, sel] as const,
    profitability: (shopId: string, sel: ReportRangeSelection) => ['reports', 'profitability', shopId, sel] as const,
    compliance: (shopId: string, sel: ReportRangeSelection) => ['reports', 'compliance', shopId, sel] as const,
    /** Metrc receipts parked for review (not range-bound: everything still open). */
    metrcNeedsReview: (shopId: string) => ['reports', 'metrc-needs-review', shopId] as const,
    delivery: (shopId: string, sel: ReportRangeSelection) => ['reports', 'delivery', shopId, sel] as const,
  },

  // Aeropay "Pay by Bank" linking + provisioning
  payments: {
    aeropayAccounts: (shopId: string, customerId: string) =>
      ['payments', 'aeropay', 'accounts', shopId, customerId] as const,
    aeropayProvisionStatus: (shopId: string) =>
      ['payments', 'aeropay', 'provision-status', shopId] as const,
    payLinkInfo: (token: string) => ['payments', 'pay-link', 'info', token] as const,
    payLinkStatus: (token: string) => ['payments', 'pay-link', 'status', token] as const,
    issues: (shopId: string) => ['payments', 'issues', shopId] as const,
  },

  // Audit / activity trail
  audit: {
    list: (params?: Record<string, unknown>) =>
      params ? (['audit', 'list', params] as const) : (['audit', 'list'] as const),
    actions: () => ['audit', 'actions'] as const,
    entity: (entityType: string, entityId: string) =>
      ['audit', 'entity', entityType, entityId] as const,
    verify: () => ['audit', 'verify'] as const,
  },

  jurisdictionRules: {
    resolved: (shopId: string) => ['jurisdictionRules', 'resolved', shopId] as const,
    forJurisdiction: (jurisdictionId: string) =>
      ['jurisdictionRules', 'jurisdiction', jurisdictionId] as const,
  },

  delivery: {
    board: (shopId: string) => ['delivery', 'board', shopId] as const,
    policy: (shopId: string) => ['delivery', 'policy', shopId] as const,
    slots: (shopId: string) => ['delivery', 'slots', shopId] as const,
    vehicles: (shopId: string) => ['delivery', 'vehicles', shopId] as const,
    failedStops: (shopId: string) => ['delivery', 'failed-stops', shopId] as const,
    manifestData: (runId: string) => ['delivery', 'manifestData', runId] as const,
  },

  queueSessions: {
    // registerId is part of the key because ownership ("mine") is computed per
    // register. Invalidations that pass only shopId still match via prefix.
    view: (shopId: string, registerId?: string | null) =>
      registerId
        ? (['queueSessions', 'view', shopId, registerId] as const)
        : (['queueSessions', 'view', shopId] as const),
    list: (shopId: string, params?: Record<string, unknown>) =>
      params ? (['queueSessions', 'list', shopId, params] as const) : (['queueSessions', 'list', shopId] as const),
    detail: (id: string) => ['queueSessions', 'detail', id] as const,
  },
} as const
